import { ApolloProvider } from '@apollo/client';
import type { FC, PropsWithChildren } from 'react';
import { Fragment, useEffect } from 'react';
import { IntlProvider } from 'react-intl';

import {
  HelmetProviderBrowser,
  HelmetProviderServer,
} from '@xing-com/crate-helmet';
import type { HeadElement, Runtime } from '@xing-com/crate-xinglet/internal';
import { RuntimeProvider } from '@xing-com/crate-xinglet/internal';

import { createApolloClient } from './create-apollo-client';

const LoadingStateHandler: React.FC<{ runtime: Runtime }> = ({ runtime }) => {
  useEffect(() => {
    function startWaiting(name: string): void {
      const eventual = runtime.cachedXinglets.get(name);
      if (!eventual || eventual.settled) return;
      if (waitingFor.add(name)) eventual.then(() => stopWaiting(name));
    }

    function stopWaiting(name: string): void {
      if (waitingFor.delete(name) && waitingFor.size === 0) {
        mountpoint?.setAttribute('data-xinglets-settled', `${counter++}`);
      }
    }

    function onXingletRequested(name: string): void {
      startWaiting(name);
    }

    let counter = 0;
    const mountpoint = document.querySelector('#app[data-mountpoint]');
    const waitingFor = new Set<string>();

    for (const name of runtime.cachedXinglets.keys()) {
      startWaiting(name);
    }

    if (waitingFor.size === 0) {
      mountpoint?.setAttribute('data-xinglets-settled', `${counter++}`);
    }

    runtime.events.on('xinglet-requested', onXingletRequested);

    return () => {
      runtime.events.off('xinglet-requested', onXingletRequested);
    };
  });

  return <></>;
};

export function createEntryPoint(runtime: Runtime, name: string): JSX.Element {
  const { language } = runtime.config;
  const { languageData: messages = {} } = runtime;
  const apolloClient = createApolloClient(runtime);

  const HelmetProvider: FC<PropsWithChildren> = runtime.isServer
    ? ({ children }) => {
        const context = {
          addElements(elements: HeadElement[]) {
            runtime.response.headElements.push(...elements);
          },
        };

        return (
          <HelmetProviderServer context={context}>
            {children}
          </HelmetProviderServer>
        );
      }
    : HelmetProviderBrowser;

  return (
    <ApolloProvider client={apolloClient}>
      <HelmetProvider>
        <IntlProvider
          locale={language}
          messages={messages}
          textComponent={Fragment}
        >
          <RuntimeProvider runtime={runtime}>
            <LoadingStateHandler runtime={runtime} />
            <runtime.host.XingletLoader runtime={runtime} name={name} />
          </RuntimeProvider>
        </IntlProvider>
      </HelmetProvider>
    </ApolloProvider>
  );
}
